import {gsap} from "../../../libs/gsap/all.js"
import * as PIXI from "pixi.js";
import 'pixi.js/math-extras'
import {BaseMiniGame} from "./BaseMiniGame";
import {GameBDraggableItem} from "./components/GameBDraggableItem";
import {Bubble} from "./components/Bubble";
import {GameBMainCharacter} from "./components/GameBMainCharacter";
import {TextPopup} from "./components/TextPopup";
import {TextIntro} from "./components/TextIntro";
import {FadeUpSprite} from "./components/FadeUpSprite";
import {Earth} from "./components/Earth";
import {Bird} from "./components/Bird";
import {Cloud} from "./components/Cloud";
import {Tree} from "./components/Tree";
import {LanguageSwitcher} from "./components/LanguageSwitcher";
import {AnimatedSprite} from "./components/AnimatedSprite";
import {Howl} from "howler";

export class GameB extends BaseMiniGame {

  // Constructor
  constructor(data) {
    super(data);
    this.appWidth = this.data.appDimensions.width;
    this.appHeight = this.data.appDimensions.height;

    this.mainTimeline = false;
    this.outTimeline = false;
    this.mainTimelineTimeheadTween = false;
    this.gameCompleted = false;
    this.gameStarted = false;

    this.draggableItems = [];
    this.currentDraggable = 0;
    this.currentTargetProgress = 0;

    this.maxX = 0; // this will be set by the background layer size

    this.layersSpeed = {
      "backgroundLayer": 1, // keep this to 1 always as this is the "base" speed
      "floorLayer": 1,
      "treesLayer": 1.10,
      "pinesLayer": 1.12,
      "cloudsBackgroundLayer": 1.25,
      "photosLayer": 1.025,
      "photosCreditsLayer": 1.025,
      "textLayer": 1,
      "textIntroLayer": 1,
      "birdLayer": 1,
      "tractorLayer": 1,
      "cowLayer": 1,
      "bubblesLayer": 1.15,
      "cloudsLayer": 1.25,
      "foregroundTreesLayer": 1.35,
      "marketLayer": 1.85
    };

    this.bipSound = new Howl({
      src: ["audio/bip.oga"],
      loop: false,
      autoplay: false,
    });

    this.completeSound = new Howl({
      src: ["audio/MAF_Themes_Ahhh.oga"],
      loop: false,
      autoplay: false,
      volume: 1,
    });
  }

  async assetLoader() {
    // Game related assets
    if (this.data.medias && this.data.medias.length) {
      for (let i = 0; i < this.data.medias.length; i++) {
        this.assets[this.data.medias[i].name] = await PIXI.Assets.load(this.data.medias[i].url);
      }
    }

    // Global assets
    if (app.data.global.medias && app.data.global.medias.length) {
      for (let i = 0; i < app.data.global.medias.length; i++) {
        this.assets[app.data.global.medias[i].name] = await PIXI.Assets.load(app.data.global.medias[i].url);
      }
    }
  }

  async buildStage() {

    this.stage.eventMode = 'static';

    this.mainContainer = new PIXI.Container();

    this.stage.addChild(this.mainContainer);

    this.buildBackground();
    this.buildOutBackground();
    this.buildCloudsBackgroundLayer();
    this.buildPhotosLayer();

    this.buildTreesLayer();
    this.buildFloorLayer();

    this.buildBubblesLayer();
    this.buildBirdsLayer();

    this.buildCloudsLayer();
    this.buildCowLayer();
    this.buildTractorLayer();

    this.buildTextIntroLayer();
    this.buildTextPopupLayer();
    this.buildPhotoCreditsLayer();

    this.buildSoundscape();

    this.buildCharacterLayer();
    this.buildForegroundTreesLayer();
    this.buildMarketLayer();

    this.buildAppTitle();
    this.buildUI();

    this.buildOverlay();

    this.buildMaintimeline();
    this.buildOutTimeline();
  }

  buildBackground() {

    this.backgroundLayer = new PIXI.Container();
    this.backgroundLayer.eventMode = 'none';
    this.stage.addChild(this.backgroundLayer);

    for (let i = 1; i <= 8; i++) {
      let bgPart = new PIXI.Sprite(this.assets["background-" + i]);
      this.backgroundLayer.addChild(bgPart);
      bgPart.position.x = bgPart.width * (i - 1);
    }

    this.maxX = this.backgroundLayer.width;
  }

  buildOutBackground() {

    this.outBackgroundLayer = new PIXI.Container();
    this.outBackgroundLayer.eventMode = 'none';
    this.stage.addChild(this.outBackgroundLayer);

    for (let i = 1; i <= 2; i++) {
      let bgPart = new PIXI.Sprite(this.assets["out-background-" + i]);
      this.outBackgroundLayer.addChild(bgPart);
      bgPart.position.x = bgPart.width * (i - 1);
    }

    this.outBackgroundLayer.position.x = this.appWidth;
  }

  buildPhotosLayer() {

    this.photosLayer = new PIXI.Container();
    this.photosLayer.eventMode = 'none';
    this.stage.addChild(this.photosLayer);

    this.photos = [];
    const photos = [
      [2964 * this.layersSpeed["photosLayer"], 340, this.assets["photo-1"], 0.9, this],
      [6610 * this.layersSpeed["photosLayer"], 175, this.assets["photo-2"], 0.9, this],
      [11500 * this.layersSpeed["photosLayer"], 410, this.assets["photo-3"], 0.9, this]
    ];
    for (let i = 0; i < photos.length; i++) {
      const photo = new FadeUpSprite(...photos[i]);
      this[`photo${i + 1}`] = photo;
      this.photos.push(photo);
      this.photosLayer.addChild(photo);
    }

    // Let's add the credits layer here so we are at the right zIndex
    this.photosCreditsLayer = new PIXI.Container();
    this.stage.addChild(this.photosCreditsLayer);
  }

  buildBubblesLayer() {

    this.bubblesLayer = new PIXI.Container();
    this.bubblesLayer.eventMode = 'none';
    this.stage.addChild(this.bubblesLayer);

    this.bubbles = [];
    const bubbles = [
      [-260, 570, 532, 0xFF995A, 0.95, this], // orange
      [1946, -450, 748, 0xFF6463, 0.95, this], // red
      [2678, 166, 178, 0xA4CEA2, 0.95, this], // green
      [3550 * this.layersSpeed["bubblesLayer"], 836, 294, 0xFF6463, 0.95, this], // red
      [6050 * this.layersSpeed["bubblesLayer"], 698, 52, 0x7ABECC, 0.95, this], // blue
      [6125 * this.layersSpeed["bubblesLayer"], 725, 748, 0xFF6463, 0.95, this], // red
      [8150 * this.layersSpeed["bubblesLayer"], 182, 52, 0x7ABECC, 0.95, this], // blue
      [8225 * this.layersSpeed["bubblesLayer"], -850, 932, 0xA4CEA2, 0.95, this], // green
      [8325 * this.layersSpeed["bubblesLayer"], 946, 56, 0xFF6463, 0.95, this], // red
      [10960 * this.layersSpeed["bubblesLayer"], 580, 110, 0xA4CEA2, 0.95, this], // green
      [11010 * this.layersSpeed["bubblesLayer"], 636, 464, 0xA4CEA2, 0.95, this], // green
      [13100 * this.layersSpeed["bubblesLayer"], 208, 86, 0xFF6463, 0.95, this], // red
      [14350 * this.layersSpeed["bubblesLayer"], -574, 596, 0xF6E4DD, 0.95, this], // pink
      [14875 * this.layersSpeed["bubblesLayer"], 658, 146, 0x7ABECC, 0.95, this], // blue
      [15075 * this.layersSpeed["bubblesLayer"], 174, 320, 0xF6E4DD, 0.95, this], // pink
      [17450 * this.layersSpeed["bubblesLayer"], -1632, 1862, 0x869E80, 1.0, this] // green
    ];
    for (let i = 0; i < bubbles.length; i++) {
      const bubble = new Bubble(...bubbles[i]);
      this.bubbles.push(bubble);
      this.bubblesLayer.addChild(bubble);
    }

    // this.earth = new Earth(13900 * this.layersSpeed["bubblesLayer"], -332, this.assets["earth"], 1, this);
    // this.earth.sprite.setSize(1420, 1420);
    // this.bubblesLayer.addChild(this.earth);
    this.earth = new Earth(this.appWidth - 1000, -187, this.assets["earth"], 1, this);
    this.stage.addChild(this.earth);
  }

  buildCloudsBackgroundLayer() {

    // Some bubbles are part of the background
    this.cloudsBackgroundLayer = new PIXI.Container();
    this.cloudsBackgroundLayer.eventMode = "none";
    this.stage.addChild(this.cloudsBackgroundLayer);

    this.clouds = [];
    const clouds = [
      [3575 * this.layersSpeed["cloudsBackgroundLayer"], 796, PIXI.Texture.from("cloud-10"), 1, this]
    ];
    for (let i = 0; i < clouds.length; i++) {
      const cloud = new Cloud(...clouds[i]);
      this.clouds.push(cloud);
      this.cloudsBackgroundLayer.addChild(cloud);
    }

  }

  buildCloudsLayer() {

    this.cloudsLayer = new PIXI.Container();
    this.cloudsLayer.eventMode = "none";
    this.stage.addChild(this.cloudsLayer);

    const clouds = [
      [476, 860, PIXI.Texture.from("cloud-8"), 1, this],
      [2754, 1210, PIXI.Texture.from("cloud-9"), 1, this],
      [3114, 1530, PIXI.Texture.from("cloud-9"), 1, this],
      [3800 * this.layersSpeed["cloudsLayer"], 1434, PIXI.Texture.from("cloud-11"), 1, this],
      [4250 * this.layersSpeed["cloudsLayer"], 1300, PIXI.Texture.from("cloud-9"), 1, this],
    ];
    for (let i = 0; i < clouds.length; i++) {
      const cloud = new Cloud(...clouds[i]);
      this.clouds.push(cloud);
      this.cloudsLayer.addChild(cloud);
    }

  }

  buildBirdsLayer() {

    this.birdLayer = new PIXI.Container();
    this.birdLayer.eventMode = "none";
    this.stage.addChild(this.birdLayer);

    this.birds = [];
    const birds = [
      [980, 526, PIXI.Texture.from("bird-1"), 1.0, this]
    ];
    for (let i = 0; i < birds.length; i++) {
      const bird = new Bird(...birds[i]);
      this.birds.push(bird);
      this.birdLayer.addChild(bird);
    }

  }

  buildTreesLayer() {

    // Pines
    this.pinesLayer = new PIXI.Container();
    this.pinesLayer.eventMode = 'none';
    this.stage.addChild(this.pinesLayer);

    const pinePart1 = new PIXI.Sprite(this.assets["pines-1"]);
    pinePart1.position.set(9750 * this.layersSpeed["pinesLayer"], 1244);
    this.pinesLayer.addChild(pinePart1);

    const pinePart2 = new PIXI.Sprite(this.assets["pines-2"]);
    pinePart2.position.set(14450 * this.layersSpeed["pinesLayer"], 1272);
    this.pinesLayer.addChild(pinePart2);

    // Trees
    this.treesLayer = new PIXI.Container();
    this.treesLayer.eventMode = 'none';
    this.stage.addChild(this.treesLayer);

    const treePart1 = new PIXI.Sprite(this.assets["trees-1"]);
    treePart1.position.set(500, 967);
    this.treesLayer.addChild(treePart1);

    const treePart2 = new PIXI.Sprite(this.assets["trees-2"]);
    treePart2.position.set(9450 * this.layersSpeed["treesLayer"], 700);
    this.treesLayer.addChild(treePart2);

  }

  buildFloorLayer() {

    this.floorLayer = new PIXI.Container();
    this.floorLayer.eventMode = 'none';
    this.stage.addChild(this.floorLayer);

    let newFloorPosition = 0;
    for (let i = 1; i <= 8; i++) {
      let floorPart = new PIXI.Sprite(this.assets["floor-" + i]);
      this.floorLayer.addChild(floorPart);

      floorPart.position.x = newFloorPosition;
      floorPart.position.y = this.appHeight - floorPart.height;
      newFloorPosition = newFloorPosition + floorPart.width;
    }

  }

  buildCowLayer() {

    this.cowLayer = new PIXI.Container();
    this.cowLayer.eventMode = 'none';
    this.stage.addChild(this.cowLayer);

    this.cows = [];
    const cows = [
      // OBJ // SIZES
      [[1264, 1618, PIXI.Texture.from("cow-5"), 1.0, this], [294, 184]],
      [[1614, 1698, PIXI.Texture.from("cow-5"), 1.0, this], [422, 264]],
      [[2758, 1632, PIXI.Texture.from("cow-6"), 1.0, this], [342, 214]],
      [[3350, 1667, PIXI.Texture.from("cow-3"), 1.0, this], [298, 178]],
      [[3572, 1536, PIXI.Texture.from("cow-5"), 1.0, this], [214, 134]],
      [[3766, 1667, PIXI.Texture.from("cow-2"), 1.0, this], [298, 178]],
      [[4078, 1552, PIXI.Texture.from("cow-1"), 1.0, this], [164, 98]],
      [[4358, 1576, PIXI.Texture.from("cow-5"), 1.0, this], [214, 134]],
      [[4538, 1632, PIXI.Texture.from("cow-6"), 1.0, this], [342, 214]],
      [[4938, 1667, PIXI.Texture.from("cow-1"), 1.0, this], [298, 178]],
      [[5098, 1571, PIXI.Texture.from("cow-3"), 1.0, this], [197, 118]],
      [[5424, 1552, PIXI.Texture.from("cow-1"), 1.0, this], [164, 98]],
      [[6340, 1667, PIXI.Texture.from("cow-1"), 1.0, this], [298, 178]],
      [[6814, 1552, PIXI.Texture.from("cow-2"), 1.0, this], [164, 98]],
      [[6978, 1667, PIXI.Texture.from("cow-4"), 1.0, this], [298, 178]],
      [[7220, 1734, PIXI.Texture.from("cow-2"), 1.0, this], [298, 178]],
      [[7782, 1667, PIXI.Texture.from("cow-6"), 1.0, this], [342, 214]],
      [[8208, 1590, PIXI.Texture.from("horse-1"), 1.0, this], [360, 280]],
      [[8686, 1571, PIXI.Texture.from("cow-4"), 1.0, this], [197, 118]],
      [[8970, 1632, PIXI.Texture.from("cow-6"), 1.0, this], [342, 214]],
      [[9370, 1667, PIXI.Texture.from("cow-1"), 1.0, this], [298, 178]],
      [[9656, 1571, PIXI.Texture.from("cow-3"), 1.0, this], [197, 118]],
      [[11904, 1496, PIXI.Texture.from("cow-6"), 1.0, this], [117, 75]],
      [[12236, 1736, PIXI.Texture.from("cow-1"), 1.0, this], [298, 178]]
    ]
    for (let i = 0; i < cows.length; i++) {
      const cow = new AnimatedSprite(...cows[i][0]);
      cow.sprite.setSize(cows[i][1][0], cows[i][1][1]);

      // Disable animation
      cow.inAnimation = false;
      cow.alpha = 1.0;

      this.cows.push(cow);
      this.cowLayer.addChild(cow);
    }
  }

  buildTractorLayer() {

    this.tractorLayer = new PIXI.Container();
    this.tractorLayer.eventMode = 'none';
    this.stage.addChild(this.tractorLayer);

    this.tractors = [];
    const tractors = [
      // OBJ // SIZES // ROTATION
      [[1040, 1357, PIXI.Texture.from("tractor-1"), 1.0, this], [336, 155], 0.147],
      [[2511, 1485, PIXI.Texture.from("tractor-2"), 1.0, this], [210, 148], -0.068],
      [[4618, 1439, PIXI.Texture.from("tractor-2"), 1.0, this], [231, 162], 0.0026],
      [[10340, 1480, PIXI.Texture.from("tractor-3"), 1.0, this], [512, 304], 0.073],
      [[14650, 1505, PIXI.Texture.from("tractor-4"), 1.0, this], [231, 162], -0.1352],
      [[15899, 1470, PIXI.Texture.from("tractor-5"), 1.0, this], [668, 248], -0.0935],

      // Haystacks
      [[13740, 1456, PIXI.Texture.from("haystack"), 1.0, this], [126, 80]],
      [[13904, 1630, PIXI.Texture.from("haystack"), 1.0, this], [164, 104]],
      [[14208, 1646, PIXI.Texture.from("haystack"), 1.0, this], [204, 130]],
      [[16886, 1438, PIXI.Texture.from("haystack"), 1.0, this], [204, 130]],
      [[17066, 1438, PIXI.Texture.from("haystack"), 1.0, this], [204, 130]],
      [[17266, 1366, PIXI.Texture.from("haystack"), 1.0, this], [128, 82]],
    ];
    for (let i = 0; i < tractors.length; i++) {
      const tractor = new AnimatedSprite(...tractors[i][0]);

      // Apply size
      tractor.sprite.setSize(tractors[i][1][0], tractors[i][1][1]);

      // Apply rotation if defined
      if (tractors[i][2] !== undefined) {
        tractor.rotation = tractors[i][2];
      }

      // Disable animation
      tractor.inAnimation = false;
      tractor.alpha = 1.0;

      this.tractors.push(tractor);
      this.tractorLayer.addChild(tractor);
    }
  }

  buildForegroundTreesLayer() {

    this.foregroundTreesLayer = new PIXI.Container();
    this.foregroundTreesLayer.eventMode = 'none';
    this.stage.addChild(this.foregroundTreesLayer);

    // this.trees = [];
    const trees = [
      [10800 * this.layersSpeed["foregroundTreesLayer"], 898, this.assets["trees-foreground"], 1.0, this]
    ];
    for (let i = 0; i < trees.length; i++) {
      const tree = new Tree(...trees[i]);
      // this.trees.push(tree);
      this.foregroundTreesLayer.addChild(tree);
    }
  }

  buildMarketLayer() {
    this.marketLayer = new PIXI.Container();
    this.marketLayer.eventMode = 'none';
    this.stage.addChild(this.marketLayer);

    this.market = new Tree(5000 * this.layersSpeed["marketLayer"], 1734, this.assets["market"], 1.0, this);
    this.marketLayer.addChild(this.market);
  }

  buildTextIntroLayer() {
    // Containers
    this.textIntroLayer = new TextIntro(1, this.data.screens[0], this);
    this.textIntroLayer.eventMode = 'none';
    this.stage.addChild(this.textIntroLayer);
  }

  buildTextPopupLayer() {
    super.buildTextPopupLayer();

    const text1 = new TextPopup((this.appWidth * 1) + (this.appWidth * 0.43), 596, "#F6E4DD", 0.95, this.data.screens[1], this);
    this.textPopups.push(text1);
    if (text1.getText()) {
      // reduced position using text width
      text1.getText().position.x -= text1.getText().width / 2;
      this.textLayer.addChild(text1.getText());
    }
    if (text1.getPopup()) {
      this.popupLayer.addChild(text1.getPopup());
    }

    const text2 = new TextPopup((this.appWidth * 2) + (this.appWidth * 0.48), 596, "#F6E4DD", 0.95, this.data.screens[2], this);
    this.textPopups.push(text2);
    if (text2.getText()) {
      // reduced position using text width
      text2.getText().position.x -= text2.getText().width / 2;
      this.textLayer.addChild(text2.getText());
    }
    if (text2.getPopup()) {
      this.popupLayer.addChild(text2.getPopup());
    }

    const text4 = new TextPopup((this.appWidth * 4) + (this.appWidth * 0.05), 596, "#343B6F", 0.95, this.data.screens[4], this);
    this.textPopups.push(text4);
    if (text4.getText()) {
      // reduced position using text width
      text4.getText().position.x -= text4.getText().width / 2;
      this.textLayer.addChild(text4.getText());
    }
    if (text4.getPopup()) {
      this.popupLayer.addChild(text4.getPopup());
    }
  }

  buildCharacterLayer() {
    this.characterLayer = new PIXI.Container();
    this.stage.addChild(this.characterLayer);

    this.mainCharacter = new GameBMainCharacter(this);
    this.mainCharacter.x = 670;
    this.mainCharacter.y = 1750;
    this.characterLayer.addChild(this.mainCharacter);
  }

  buildUI() {

    this.uiLayer = new PIXI.Container();
    this.stage.addChild(this.uiLayer);

    const draggableItems = [
      ["riz", true, 0, 0, this],
      ["pois", false, -100, -20, this],
      ["mais", false, 0, 0, this],
      ["pasteque", false, 0, 0, this],
      ["legumes", false, 0, -50, this],
      ["amandes", false, 0, 0, this],
      ["fruits", false, 0, 0, this],
      ["pommes", false, 0, 0, this], // added one more
      ["patates", false, -90, 0, this], // added one more
    ];

    for (let i = 0; i < draggableItems.length; i++) {
      const draggableItem = new GameBDraggableItem(...draggableItems[i]);
      draggableItem.on("complete", this.onDraggableComplete.bind(this))
      draggableItem.on("inCart", this.onDraggableInCart.bind(this))
      draggableItem.on("progress", this.onDraggableProgress.bind(this))
      this.draggableItems.push(draggableItem);
      this.uiLayer.addChild(draggableItem);
    }

    // Add language switcher
    this.languageSwitcher = new LanguageSwitcher(this);
    this.stage.addChild(this.languageSwitcher);
  }

  onDraggableInCart() {
    // Play bip sound
    this.bipSound.play();
  }

  onDraggableComplete(data) {

    // console.log("complete", data);

    if (this.currentTargetProgress < 1) {
      this.currentDraggable++;

      if (this.currentDraggable < this.draggableItems.length) {
        this.enableUI();
      }
    } else {
      this.gameCompleted = true;
      this.disableUI();
      this.languageSwitcher.disable();
      for (let i = 0; i < this.textPopups.length; i++) {
        this.textPopups[i].disable();
      }

      this.outTimeline.play();
    }

  }

  onDraggableProgress(data) {
    if (this.gameCompleted) {
      return;
    }

    this.onTouchDown();

    const startProgress = this.currentDraggable / this.draggableItems.length;
    const targetProgress = (this.currentDraggable + 1) / this.draggableItems.length;

    let currentProgress = ((targetProgress - startProgress) * data.currentProgress) + startProgress;

    this.currentTargetProgress = targetProgress;
    super.progress(currentProgress);

    this.mainTimelineTimeheadTween = gsap.to(this.mainTimeline, {
      progress: currentProgress,
      duration: 0.5,
      delay: 0.35,
      overwrite: "auto", // smooth the animation so it's not jiggery
    })
  }

  buildMaintimeline() {
    let maxX = -this.backgroundLayer.width + this.appWidth;

    this.mainTimeline = gsap.timeline({
      paused: true,
      defaults: {
        duration: 30,
        ease: "none"
      }
    });

    for (const [name, speed] of Object.entries(this.layersSpeed)) {
      this.mainTimeline.to(this[name], {
        pixi: {
          x: maxX * speed,
        }
      }, 0);
    }

    // Slide down the earth
    this.mainTimeline.fromTo(this.earth, {
      pixi: {
        y: -2000,
      },
    }, {
      pixi: {
        y: -187,
      },
      immediateRender: true,
      ease: "power1.out",
      duration: 6,
    }, "-=6");
  }


  buildOutTimeline() {
    this.outTimeline = gsap.timeline({
      paused: true,
      defaults: {
        duration: 4,
        ease: "linear",
      },
      delay: 0.4,
      onComplete: () => {
        this.dispatchEvent(new CustomEvent("complete", {detail: "GameB Completed."}))
      },
      onStart: () => {
        this.completeSound.seek(0).play();
      },
    });

    let slideDistance = 2000;

    for (const [name, speed] of Object.entries(this.layersSpeed)) {
      this.outTimeline.to(this[name], {
        ease: "sine.in",
        pixi: {
          x: "-=" + (slideDistance * speed), // scroll 2/3 of the screen
        }
      }, 0);
    }

    this.outTimeline.to(this.outBackgroundLayer, {
      ease: "sine.in",
      pixi: {
        x: "-=" + slideDistance, // arbitrary number that worked based on above transition
      }
    }, 0);

    this.outTimeline.fromTo(this.fadeOverlayLayer, {
      pixi: {
        alpha: 0,
      }
    }, {
      pixi: {
        alpha: 1,
      },
      duration: 2,
      overwrite: "auto"
    }, 2)
  }

  activate() {
    // Enable render
    this.start();
    this.ticker.start();
    this.soundscape.start();

    // TODO: start the timelines here so they are moving while fading in view
  }

  deactivate() {
    // Disable render
    this.stop();
    this.ticker.stop();

    this.disableUI(true);
    this.languageSwitcher.disable();

    // Stop sound
    this.soundscape.stopAll()

    // Pause all the timelines so the game isn't doing a thing while not on screen

    if (this.mainTimelineTimeheadTween !== false) {
      this.mainTimelineTimeheadTween.kill();
      this.mainTimelineTimeheadTween = false;
    }

    this.mainTimeline.pause();
  }

  begin() {
    // Game is ready to go, on screen, and animations have started
    this.enableUI();
    this.languageSwitcher.enable();

    this.fadeOutOverlay();
  }

  enableUI() {
    if (this.currentDraggable < this.draggableItems.length) {
      this.draggableItems[this.currentDraggable].enable();
    }
  }

  disableUI(immediate) {
    immediate = typeof immediate !== "undefined" ? immediate : false;

    if (this.currentDraggable < this.draggableItems.length) {
      this.draggableItems[this.currentDraggable].disable(immediate);
    }
  }

  onTouchDown(e) {
    // Intro, switch text when user start moving in timeline
    if (!this.gameStarted) {
      this.textIntroLayer.nextStep();

      this.gameStarted = true;
    }
  }

  reset() {
    this.gameStarted = false;
    this.gameCompleted = false;
    this.currentDraggable = 0;
    this.currentTargetProgress = 0;

    this.outTimeline.progress(0, true);
    this.mainTimeline.progress(0, true);

    this.outTimeline.kill();
    this.buildOutTimeline();

    // Reset intro
    this.textIntroLayer.resetSteps();
    this.textIntroLayer.reset();

    // Reset birds
    for (let i = 0; i < this.birds.length; i++) {
      this.birds[i].reset();
    }

    // Reset cows
    for (let i = 0; i < this.cows.length; i++) {
      this.cows[i].reset();
    }

    // Reset tractors
    for (let i = 0; i < this.tractors.length; i++) {
      this.tractors[i].reset();
    }

    // Reset photos
    for (let i = 0; i < this.photos.length; i++) {
      this.photos[i].reset();
    }

    // Reset bubbles
    for (let i = 0; i < this.bubbles.length; i++) {
      this.bubbles[i].reset();
    }

    // Reset text popup
    for (let i = 0; i < this.textPopups.length; i++) {
      this.textPopups[i].hidePopup();
      this.textPopups[i].reset();
    }

    // Reset clouds
    for (let i = 0; i < this.clouds.length; i++) {
      this.clouds[i].reset();
    }

    // Reset draggable items
    for (let i = 0; i < this.draggableItems.length; i++) {
      this.draggableItems[i].reset();
    }

    this.market.reset();

    this.fadeOverlayLayer.alpha = 1;
  }
}
